import { useEffect, useRef, useState } from 'react';
import { LoaderPlaceholder, TableWrapper } from './Table.styles';
import { Grid, MuiTable, MuiTableBody, useTheme } from '../../base-components';
import { ITableProps, IRowData } from '../table.interfaces';
import TableHeader from './TableHeader';
import Row from './Row';
import TopActions from './TopActions';
import { useForm } from 'react-hook-form';
import { ITableForm } from '../../../common/interfaces/table.interface';
import { Form } from '../../base-forms';
import useFiltersAndSortUpdate from '../Hooks/useFiltersAndSortUpdate';
import { formatTableFormFromState } from '../table.utils';
import { useAppDispatch } from '../../base-redux';
import { tablesActions } from '../../../actions';
import TableSkeletonBody from '../Table/TableSkeletonBody';
import EmptyTableBody from './EmptyTableBody';
import useInfiniteScroll from '../Hooks/useInfiniteScroll';
import { getBasicColorsObject } from '../../../common';
import PargodaLoading from '../../../components/PargodaLoading';

const Table = (props: ITableProps) => {
    const dispatch = useAppDispatch();
    const {
        tableState,
        rowsData = [],
        structure,
        callbacks,
        emptyTableComponent,
        emptyTableMessage,
    } = props;

    const [tableMaxHeight, setTableMaxHeight] = useState(0);
    const tableRef = useRef<HTMLDivElement>(null);
    const {
        isLoading,
        isLoadingMore,
        id: tableId,
        selectedRows,
        currentPage,
        totalPages,
    } = tableState;
    const { filters = [], tableHeight, bulkActions = [], toggles = [] } = structure;
    const methods = useForm<ITableForm>({
        mode: 'onBlur',
        defaultValues: formatTableFormFromState(tableState, filters, toggles),
    });
    const { control, getValues } = methods;
    const theme = useTheme();
    const baseColors = getBasicColorsObject(theme.palette);

    const loadMoreData = () => {
        if (!isLoading && currentPage != totalPages) {
            dispatch(
                tablesActions.fetchTableDataById({
                    tableId,
                    formData: getValues(),
                    page: currentPage + 1,
                    baseColors,
                })
            ); // Dispatch your action to load more data
        }
    };

    useInfiniteScroll({
        tableRef,
        isLoading,
        isLoadingMore,
        loadMore: loadMoreData,
    });

    useFiltersAndSortUpdate(control, getValues);

    const onSelectRow = (rowId: string) => {
        const currentSelectedRows = tableState.selectedRows;

        const updatedSelectedRows = { ...currentSelectedRows };
        if (updatedSelectedRows[rowId]) {
            delete updatedSelectedRows[rowId];
        } else {
            updatedSelectedRows[rowId] = true;
        }
        const updatedTableState = {
            ...tableState,
            selectedRows: updatedSelectedRows,
        };

        dispatch(
            tablesActions.setTableStateById({
                tableId,
                tableState: updatedTableState,
            })
        );
    };

    const onSelectAllRows = () => {
        const headerCheckboxSelected =
            Object.keys(selectedRows).length ===
            tableState.currentEntitiesCount;
        let updatedTableState = tableState;
        if (headerCheckboxSelected) {
            updatedTableState = {
                ...tableState,
                selectedRows: {},
            };
        } else {
            const allRowsSelected: Record<string, boolean> = rowsData.reduce(
                (acc, row) => {
                    acc[row.id] = true;
                    return acc;
                },
                {} as Record<string, boolean>
            );
            updatedTableState = {
                ...tableState,
                selectedRows: allRowsSelected,
            };
        }
        dispatch(
            tablesActions.setTableStateById({
                tableId,
                tableState: updatedTableState,
            })
        );
    };

    const renderRow = (rowData: IRowData) => {
        const rowId = rowData.data.id;
        return (
            <Row
                key={rowId}
                rowData={rowData}
                structure={structure}
                callbacks={callbacks}
                isSelected={tableState.selectedRows[rowId]}
                onSelectionChange={onSelectRow}
            />
        );
    };

    const renderTableBody = () => {
        return (
            <MuiTableBody>
                {rowsData?.map((data, index) => renderRow({ data, index }))}
            </MuiTableBody>
        );
    };

    const renderTable = () => {
        if (isLoading) {
            return <TableSkeletonBody structure={structure} />;
        }
        if (!rowsData?.length) {
            return (
                <EmptyTableBody
                    emptyTableComponent={emptyTableComponent}
                    emptyTableMessage={emptyTableMessage}
                />
            );
        }
        return renderTableBody();
    };

    useEffect(() => {
        const offsetTop = tableRef?.current?.getBoundingClientRect().top || 0;
        const maxHeight = offsetTop + 16;
        if (offsetTop && maxHeight !== tableMaxHeight) {
            setTableMaxHeight(maxHeight);
        }
    });

    return (
        <TableWrapper>
            <Form methods={methods}>
                {!structure.noTopActions && (
                    <TopActions
                        control={methods.control}
                        filters={filters}
                        bulkActions={bulkActions}
                        toggles={toggles}
                        tableState={tableState}
                    />
                )}
                <Grid
                    overflow="auto"
                    ref={tableRef}
                    height={tableHeight ? `${tableHeight}px` : undefined}
                    maxHeight={
                        tableHeight
                            ? undefined
                            : `calc(100vh - ${tableMaxHeight}px)`
                    }
                >
                    <MuiTable sx={{ borderCollapse: 'separate' }}>
                        <TableHeader
                            structure={structure}
                            tableState={tableState}
                            onSelectAllRows={onSelectAllRows}
                        />
                        {renderTable()}
                    </MuiTable>
                    {isLoadingMore && (
                        <LoaderPlaceholder>
                            <PargodaLoading height={50} width={50} />
                        </LoaderPlaceholder>
                    )}
                </Grid>
            </Form>
        </TableWrapper>
    );
};

export default Table;
